// Daily routines on the schedule seam (decision 0049: "town NPCs follow simple daily routines tied to time of day";
// 0054: NPC schedules as in 0049). Phase D1 fills the seam phase B left (schedule.ts): a routine table answers "what
// should this agent be doing at this hour", and `stepRoutines` turns that into where each agent should stand, facing
// which way, doing what, for every agent at once, so two agents never take one spot.
//
// The model is the standard one (Stardew's schedules, The Sims' and Oblivion's packages, cut down): a routine is a
// list of blocks by hour (`RoutineBlock`: from, to, activity, place); a place is a set of spots the game resolves
// (a forge's anvil, the square's benches, an agent's own front door); an agent walks to a free spot of its block's
// place, arrives, faces the spot's way and does the activity; a wandering place moves it to another free spot now
// and then; an "inside" place takes it out of sight once it arrives (a house). When the clock jumps (a rest, a test
// setting the hour), the game may snap everyone to their spots at once.
//
// Plain data and pure functions: the state is JSON-safe for a save; spot choice is a hash of the agent, the block
// and a counter, never a random draw; nothing reads the clock. Movement is the game's (a navmesh crowd, or a
// straight line while the agent's ground isn't loaded).
import { blockAt, type RoutineBlock, type ScheduleSource } from "./schedule";

/** Planted defects beside what they break: the game's routine checks must go red with them on. */
export const ROUTINE_FAULTS = {
  /** The schedule ignores the hour: every agent stays on its routine's first block (the at-hour check must fail). */
  hourIgnored: false,
};

/** A spot in a place: where an agent stands (metres, ground plane) and which way it faces (yaw, radians; absent: it
 *  keeps the way it came). */
export interface PlaceSpot {
  x: number;
  z: number;
  yaw?: number;
}

/** A place, as the game resolves it for one agent. */
export interface RoutinePlace {
  id: string;
  /** At least one. */
  spots: readonly PlaceSpot[];
  /** Within this many metres of its spot an agent has arrived (default 0.6). */
  arrive?: number;
  /** Seconds between moves to another spot once arrived (absent: it stays on its spot). */
  wanderEvery?: number;
  /** Arrived agents are inside, out of sight (a house at night). */
  inside?: boolean;
}

/** A routine table: each agent's blocks. Implements the schedule seam. */
export class RoutineSchedule implements ScheduleSource {
  private readonly table: Map<string, readonly RoutineBlock[]>;
  constructor(routines: Record<string, readonly RoutineBlock[]>) {
    this.table = new Map(Object.entries(routines));
  }
  at(agent: string, hour: number): RoutineBlock | null {
    const r = this.table.get(agent);
    if (!r || !r.length) return null;
    if (ROUTINE_FAULTS.hourIgnored) return r[0]!;
    return blockAt(r, hour);
  }
  /** An agent's blocks (empty when it has none). */
  routine(agent: string): readonly RoutineBlock[] {
    return this.table.get(agent) ?? [];
  }
  agents(): string[] {
    return [...this.table.keys()];
  }
}

/** Problems with a routine: hours out of range, empty blocks, overlaps (each named by its block). Gaps are free time
 *  and allowed. */
export function validateRoutine(blocks: readonly RoutineBlock[]): string[] {
  const out: string[] = [];
  const span = (b: RoutineBlock): [number, number][] => (b.from <= b.to ? [[b.from, b.to]] : [[b.from, 24], [0, b.to]]);
  blocks.forEach((b, i) => {
    const name = `block ${i} (${b.activity} at ${b.place})`;
    if (!(b.from >= 0 && b.from < 24) || !(b.to >= 0 && b.to <= 24)) out.push(`${name}: hours must be in [0, 24)`);
    if (b.from === b.to) out.push(`${name}: empty (from equals to)`);
    if (!b.place) out.push(`${name}: no place`);
  });
  for (let i = 0; i < blocks.length; i++)
    for (let j = i + 1; j < blocks.length; j++)
      for (const [a0, a1] of span(blocks[i]!))
        for (const [b0, b1] of span(blocks[j]!)) if (Math.max(a0, b0) < Math.min(a1, b1) - 1e-9) out.push(`blocks ${i} and ${j} overlap`);
  return out;
}

/** One agent's routine state. Plain data (save it). */
export interface RoutineState {
  /** The block it is on (`from-to:place`), or null (free time). */
  block: string | null;
  place: string | null;
  /** Its spot in the place. */
  spot: number;
  /** Moves to another spot so far in this block (salts the next pick). */
  moves: number;
  arrived: boolean;
  /** Sim seconds when it took its spot, and when it next wanders (null: it stays). */
  since: number;
  nextMove: number | null;
}

/** A fresh state (free, nowhere). */
export const routineState = (): RoutineState => ({ block: null, place: null, spot: 0, moves: 0, arrived: false, since: 0, nextMove: null });

/** What an agent should do this step. */
export interface RoutineDecision {
  /** Where to walk (null: free time, or no such place: stay). */
  goal: PlaceSpot | null;
  /** The activity once arrived (the block's), or null. */
  activity: string | null;
  place: string | null;
  arrived: boolean;
  /** Arrived at an inside place: out of sight. */
  inside: boolean;
  /** Its block or spot changed this step. */
  changed: boolean;
  /** Snapped: the game puts it on `goal` at once. */
  snap: boolean;
}

/** One agent as the game sees it this step. */
export interface RoutineAgent {
  id: string;
  /** Feet on the ground plane. */
  x: number;
  z: number;
  /** Busy with something else (talking, in a cutscene): its routine waits (no arrival, no wandering). */
  busy?: boolean;
}

/** The step's world: the hour, sim time, the place resolver, and whether to snap everyone onto their spots. */
export interface RoutineWorld {
  /** Hours, 0 to 24. */
  hour: number;
  /** Sim seconds. */
  now: number;
  /** A place for an agent (the game resolves "home" per agent), or null when it has none. */
  place(id: string, agent: string): RoutinePlace | null;
  /** Put every agent on its spot now (a clock jump). */
  snap?: boolean;
}

/** A small stable hash (FNV-1a) for spot picks. */
export function routineHash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** The arrival distance of a place. */
export const arriveRadius = (p: RoutinePlace): number => p.arrive ?? 0.6;

/** Step every agent's routine: blocks by the hour, a free spot each (agents in the order given claim first), arrival
 *  by distance, wandering, inside places. Mutates `states` (each agent gets one if missing) and returns the decisions
 *  by agent id. */
export function stepRoutines(agents: readonly RoutineAgent[], states: Record<string, RoutineState>, schedule: ScheduleSource, w: RoutineWorld): Record<string, RoutineDecision> {
  const out: Record<string, RoutineDecision> = {};
  // spots held by agents that keep theirs this step
  const taken = new Set<string>();
  const key = (place: string, spot: number): string => `${place}#${spot}`;
  const blocks = new Map<string, RoutineBlock | null>();
  for (const a of agents) {
    const st = (states[a.id] ??= routineState());
    const b = schedule.at(a.id, w.hour);
    blocks.set(a.id, b);
    const bk = b ? `${b.from}-${b.to}:${b.place}` : null;
    if (bk === st.block && st.place) taken.add(key(st.place, st.spot));
  }
  const pick = (agent: string, place: RoutinePlace, salt: string, avoid: number | null): number => {
    const n = place.spots.length;
    const start = routineHash(`${agent}|${place.id}|${salt}`) % n;
    for (let k = 0; k < n; k++) {
      const i = (start + k) % n;
      if (i === avoid && n > 1) continue;
      if (!taken.has(key(place.id, i))) return i;
    }
    // every spot taken: share the hashed one
    return start;
  };
  for (const a of agents) {
    const st = states[a.id]!;
    const b = blocks.get(a.id)!;
    const bk = b ? `${b.from}-${b.to}:${b.place}` : null;
    let changed = false;
    const place = b ? w.place(b.place, a.id) : null;
    if (bk !== st.block) {
      st.block = bk;
      st.place = place ? place.id : null;
      st.moves = 0;
      st.arrived = false;
      st.since = w.now;
      st.spot = place ? pick(a.id, place, bk!, null) : 0;
      st.nextMove = place?.wanderEvery !== undefined ? w.now + place.wanderEvery : null;
      if (place) taken.add(key(place.id, st.spot));
      changed = true;
    }
    if (!place || !b) {
      out[a.id] = { goal: null, activity: b?.activity ?? null, place: null, arrived: false, inside: false, changed, snap: false };
      continue;
    }
    if (st.spot >= place.spots.length) st.spot = 0;
    // wander: once arrived and its time is up, on to another free spot
    if (!a.busy && st.arrived && st.nextMove !== null && w.now >= st.nextMove && place.spots.length > 1) {
      taken.delete(key(place.id, st.spot));
      st.moves++;
      st.spot = pick(a.id, place, `${bk}|${st.moves}`, st.spot);
      taken.add(key(place.id, st.spot));
      st.arrived = false;
      st.since = w.now;
      st.nextMove = place.wanderEvery !== undefined ? w.now + place.wanderEvery : null;
      changed = true;
    }
    const spot = place.spots[st.spot]!;
    const snap = w.snap === true && !a.busy;
    if (snap) st.arrived = true;
    else if (!st.arrived && !a.busy && Math.hypot(a.x - spot.x, a.z - spot.z) <= arriveRadius(place)) {
      st.arrived = true;
      st.since = w.now;
      if (place.wanderEvery !== undefined) st.nextMove = w.now + place.wanderEvery;
    }
    out[a.id] = { goal: spot, activity: b.activity, place: place.id, arrived: st.arrived, inside: st.arrived && place.inside === true, changed, snap };
  }
  return out;
}

/** The place an agent is at (arrived), or null while it walks or has none: the game's `npc.<id>.place` fact. */
export const placeOf = (st: RoutineState | undefined): string | null => (st && st.arrived ? st.place : null);
